import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/ui/Modal/Modal";
import Button from "@/ui/Button/Button";
import { setUserPromocode } from "@/api/user";
import { useAuth } from "@/store/authStore";
import { useUserDataContext } from "@/store/UserProvider";
import cls from "./styles.module.scss";

const PROMO_MAX_LENGTH = 128;

export default function PromoCodeModal() {
  const { t } = useTranslation();
  const { user, refreshUser } = useUserDataContext();
  const { initData, sessionToken } = useAuth();
  const [promo, setPromo] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const trimmedPromo = useMemo(() => promo.trim(), [promo]);
  const isBrowserSravniDohodUser =
    __SERVICE_NAME__ === "sravni_dohod" && !initData && Boolean(sessionToken);

  useEffect(() => {
    if (!user || user.promocode_modal_shown || !isBrowserSravniDohodUser)
      return;
    setIsOpen(true);
  }, [isBrowserSravniDohodUser, user]);

  const finish = async (nextPromo: string | null) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await setUserPromocode({ promocode: nextPromo });
      setIsSaved(Boolean(nextPromo));
      await refreshUser(true);

      window.setTimeout(
        () => {
          setIsOpen(false);
        },
        nextPromo ? 650 : 0
      );
    } catch (error) {
      console.error("Failed to save promo", error);
    } finally {
      setIsSaving(false);
    }
  };

  const onPromoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPromo(event.target.value.slice(0, PROMO_MAX_LENGTH));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => finish(null)}
      hideCloseBtn
      closeOnBackdropClick={false}
      overlayCls={cls.overlay}
      wrapperCls={cls.wrapper}
      contentCls={cls.content}
    >
      <div className={cls.grab} />
      <div className={cls.icon}>🎁</div>
      <h2 className={cls.title}>{t("promoCodeModal.title")}</h2>
      <p className={cls.description}>{t("promoCodeModal.description")}</p>

      <div className={cls.inputWrap}>
        <input
          className={cls.input}
          value={promo}
          maxLength={PROMO_MAX_LENGTH}
          placeholder={t("promoCodeModal.placeholder")}
          autoFocus
          disabled={isSaving || isSaved}
          onChange={onPromoChange}
          onKeyDown={(event) => {
            if (event.key === "Enter" && trimmedPromo) {
              finish(trimmedPromo);
            }
          }}
        />
      </div>

      <div className={cls.status}>
        {isSaved && t("promoCodeModal.savedStatus")}
      </div>

      <Button
        className={isSaved ? cls.savedButton : cls.applyButton}
        label={
          isSaved
            ? t("promoCodeModal.appliedButton")
            : t("promoCodeModal.applyButton")
        }
        onClick={() => finish(trimmedPromo)}
        disabled={!trimmedPromo || isSaved}
        loading={isSaving}
      />
      <button
        className={cls.skipButton}
        type="button"
        disabled={isSaving}
        onClick={() => finish(null)}
      >
        {t("promoCodeModal.skipButton")}
      </button>
    </Modal>
  );
}
